/**
 * Location Service — GPS polling + Firestore write for live team positions
 *
 * Polls navigator.geolocation.getCurrentPosition() every 30 seconds while the
 * browser tab is visible. Writes to Firestore tech_states/{userId}.liveLocation.
 * Pauses when tab is hidden to conserve battery. Cleans up on unmount.
 *
 * NOT a watchPosition() — periodic polling is more battery-friendly on mobile.
 */

import { db } from '@/firebase';
import { doc, setDoc, updateDoc, deleteField } from 'firebase/firestore';
import {
  GPS_POLL_INTERVAL_MS,
  MIN_DISTANCE_CHANGE_M,
  LOCATION_HEARTBEAT_MS,
} from '@/data/map-config';

// ═══════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════

/** Haversine distance in meters between two lat/lng points */
export function distanceMeters(
  lat1: number, lng1: number,
  lat2: number, lng2: number
): number {
  const R = 6371000; // Earth radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// ═══════════════════════════════════════
// CORE SERVICE
// ═══════════════════════════════════════

interface BroadcastState {
  intervalId: ReturnType<typeof setInterval> | null;
  lastLat: number | null;
  lastLng: number | null;
  lastWriteMs: number | null; // when we last wrote (full move OR heartbeat)
  isPaused: boolean;
}

/**
 * Start broadcasting the current user's GPS position to Firestore.
 * Returns a cleanup function that stops broadcasting. The last-seen position is
 * intentionally retained so the user persists on the map as "last seen".
 */
export function startLocationBroadcast(
  userId: string,
  userName: string,
  userRole: string,
  tenantId: string
): () => void {
  if (!navigator.geolocation) {
    return () => {}; // Geolocation not supported — silent no-op
  }

  const state: BroadcastState = {
    intervalId: null,
    lastLat: null,
    lastLng: null,
    lastWriteMs: null,
    isPaused: false,
  };

  const docRef = doc(db, 'tech_states', userId);

  const writeLocation = (lat: number, lng: number, accuracy?: number) => {
    const moved =
      state.lastLat === null ||
      state.lastLng === null ||
      distanceMeters(state.lastLat, state.lastLng, lat, lng) >= MIN_DISTANCE_CHANGE_M;

    const nowMs = Date.now();

    if (!moved) {
      // Stationary — don't re-write the position, but refresh lastSeen as a
      // lightweight heartbeat so the person still reads as "online" on the map
      // even when they haven't moved. Throttled to avoid Firestore write spam.
      if (state.lastWriteMs === null || nowMs - state.lastWriteMs >= LOCATION_HEARTBEAT_MS) {
        state.lastWriteMs = nowMs;
        // Dotted-path update is safe here: the first poll always "moves"
        // (lastLat === null) and writes the full doc, so liveLocation exists.
        updateDoc(docRef, { 'liveLocation.lastSeen': new Date().toISOString() }).catch(() => {
          // Silent — network errors are expected on mobile
        });
      }
      return;
    }

    state.lastLat = lat;
    state.lastLng = lng;
    state.lastWriteMs = nowMs;

    setDoc(
      docRef,
      {
        tenantId,
        liveLocation: {
          lat,
          lng,
          lastSeen: new Date().toISOString(),
          userName,
          userRole,
          ...(accuracy != null ? { accuracy } : {}),
        },
      },
      { merge: true }
    ).catch(() => {
      // Silent — network errors are expected on mobile
    });
  };

  const poll = () => {
    if (state.isPaused) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        writeLocation(
          position.coords.latitude,
          position.coords.longitude,
          position.coords.accuracy
        );
      },
      () => {
        // Permission denied or position unavailable — silent
      },
      {
        enableHighAccuracy: true,
        timeout: 10_000,
        maximumAge: 15_000,
      }
    );
  };

  // Visibility change handler — pause/resume polling
  const handleVisibility = () => {
    if (document.visibilityState === 'hidden') {
      state.isPaused = true;
    } else {
      state.isPaused = false;
      poll(); // Immediately poll on resume
    }
  };

  document.addEventListener('visibilitychange', handleVisibility);

  // Initial poll + start interval
  poll();
  state.intervalId = setInterval(poll, GPS_POLL_INTERVAL_MS);

  // Return cleanup function
  return () => {
    if (state.intervalId) clearInterval(state.intervalId);
    document.removeEventListener('visibilitychange', handleVisibility);

    // Intentionally KEEP the last liveLocation in Firestore so the person stays
    // on the map at their last-seen position. The map dims/labels them as
    // "last seen X ago" based on the lastSeen timestamp — it no longer deletes
    // the marker the moment the app closes. (See useTeamLocations tiers.)
  };
}

/**
 * Explicitly remove the current user from the live map by clearing their
 * liveLocation field. NOT called by normal cleanup/logout (people now persist
 * as "last seen"); reserve for an explicit "stop sharing my location" opt-out.
 */
export async function stopLocationBroadcast(userId: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'tech_states', userId), {
      liveLocation: deleteField(),
    });
  } catch {
    // Silent
  }
}
